import React from 'react';
import { BUILDING_TYPE_PRESETS } from '../../constants/structural';

const SelectIcon = ({ color = '#6080a0', size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4l7 16 2-6 6-2L4 4z" fill={color} fillOpacity="0.15" />
    <path d="M4 4l7 16 2-6 6-2L4 4z" />
    <path d="M13 13l6 6" />
  </svg>
);

const JoistCalcIcon = ({ color = '#6080a0', size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* I-beam cross section */}
    <rect x="4" y="4" width="16" height="3" rx="0.5" fill={color} fillOpacity="0.3" />
    <rect x="4" y="17" width="16" height="3" rx="0.5" fill={color} fillOpacity="0.3" />
    <rect x="10" y="7" width="4" height="10" fill={color} fillOpacity="0.15" />
    <rect x="4" y="4" width="16" height="3" rx="0.5" />
    <rect x="4" y="17" width="16" height="3" rx="0.5" />
    <rect x="10" y="7" width="4" height="10" />
    {/* dimension arrows */}
    <line x1="2" y1="5.5" x2="2" y2="18.5" strokeDasharray="1.5 1" />
    <path d="M1 6l1-1.5 1 1.5M1 18l1 1.5 1-1.5" />
  </svg>
);

const BuildingIcon = ({ color = '#6080a0', size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" fill={color} fillOpacity="0.1" />
    <path d="M5 21V7l7-4 7 4v14" />
    <rect x="9" y="13" width="6" height="8" fill={color} fillOpacity="0.2" />
    <rect x="9" y="13" width="6" height="8" />
    <line x1="12" y1="13" x2="12" y2="21" />
  </svg>
);

const DrawJoistsIcon = ({ color = '#6080a0', size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
    {/* Bay rectangle outline */}
    <rect x="3" y="3" width="18" height="18" rx="0.5" strokeDasharray="2 1.5" />
    {/* Parallel joist lines */}
    <line x1="3" y1="8" x2="21" y2="8" />
    <line x1="3" y1="13" x2="21" y2="13" />
    <line x1="3" y1="18" x2="21" y2="18" />
    {/* Spacing tick marks */}
    <line x1="7" y1="6" x2="7" y2="10" strokeWidth="1" />
    <line x1="12" y1="6" x2="12" y2="10" strokeWidth="1" />
    <line x1="17" y1="6" x2="17" y2="10" strokeWidth="1" />
  </svg>
);

const BeamCalcIcon = ({ color = '#6080a0', size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Beam with supports */}
    <rect x="2" y="10" width="20" height="4" rx="0.5" fill={color} fillOpacity="0.2" />
    <rect x="2" y="10" width="20" height="4" rx="0.5" />
    {/* Support triangles */}
    <polygon points="4,14 6,14 5,18" fill={color} fillOpacity="0.3" />
    <polygon points="4,14 6,14 5,18" />
    <polygon points="18,14 20,14 19,18" fill={color} fillOpacity="0.3" />
    <polygon points="18,14 20,14 19,18" />
    {/* Load arrows */}
    <line x1="8" y1="4" x2="8" y2="10" strokeDasharray="2 1" />
    <line x1="12" y1="4" x2="12" y2="10" strokeDasharray="2 1" />
    <line x1="16" y1="4" x2="16" y2="10" strokeDasharray="2 1" />
    <path d="M7 8l1 2 1-2" />
    <path d="M11 8l1 2 1-2" />
    <path d="M15 8l1 2 1-2" />
  </svg>
);

const StructuralToolbar = ({
  activeTool,
  onToolChange,
  buildingType,
  onBuildingTypeChange,
  onShowJoistCalc,
  onShowBeamCalc,
  showJoistCalc = false,
  showBeamCalc = false,
  isMobile = false,
  onClose,
}) => {
  const buttonStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '2px' : '4px',
    padding: isMobile ? '6px 8px' : '8px 12px',
    minWidth: isMobile ? '44px' : 'auto',
    background: active ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${active ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '6px',
    color: active ? '#4caf50' : '#8090a0',
    fontSize: isMobile ? '9px' : '12px',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  });

  const sectionStyle = isMobile ? {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  } : {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 8px',
    borderRight: '1px solid rgba(255,255,255,0.1)',
  };

  const labelStyle = {
    color: '#6080a0',
    fontSize: '10px',
    marginRight: '4px',
  };

  const selectStyle = {
    padding: isMobile ? '4px 6px' : '6px 8px',
    background: '#1a1f24',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: isMobile ? '10px' : '11px',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '4px' : '8px',
        padding: isMobile ? '6px 8px' : '8px 12px',
        background: 'rgba(0,0,0,0.3)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        ...(isMobile ? {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          flexWrap: 'nowrap',
        } : {
          flexWrap: 'wrap',
        }),
      }}
    >
      {/* Exit Structural Mode - mobile only */}
      {isMobile && onClose && (
        <div style={sectionStyle}>
          <button
            onClick={onClose}
            style={{
              ...buttonStyle(false),
              background: 'rgba(230, 81, 0, 0.3)',
              borderColor: '#e65100',
              color: '#ff9800',
            }}
            title="Exit Structural Mode"
          >
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#ff9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Select Tool */}
      <div style={sectionStyle}>
        <button
          onClick={() => onToolChange('select')}
          style={buttonStyle(activeTool === 'select')}
          title="Select"
        >
          <SelectIcon color={activeTool === 'select' ? '#4caf50' : '#6080a0'} size={isMobile ? 16 : 18} />
          {!isMobile && <span>Select</span>}
        </button>
      </div>

      {/* Draw Joists Tool */}
      <div style={sectionStyle}>
        <button
          onClick={() => onToolChange('joist')}
          style={buttonStyle(activeTool === 'joist')}
          title="Draw Joist Bay"
        >
          <DrawJoistsIcon color={activeTool === 'joist' ? '#4caf50' : '#6080a0'} size={isMobile ? 16 : 18} />
          <span>{isMobile ? 'Joists' : 'Draw Joists'}</span>
        </button>
      </div>

      {/* Building Type */}
      <div style={isMobile ? sectionStyle : { ...sectionStyle }}>
        {!isMobile && <BuildingIcon color="#6080a0" size={16} />}
        {!isMobile && <span style={labelStyle}>Type:</span>}
        <select
          value={buildingType}
          onChange={(e) => onBuildingTypeChange(e.target.value)}
          style={selectStyle}
        >
          {Object.entries(BUILDING_TYPE_PRESETS).map(([key, preset]) => (
            <option key={key} value={key}>{preset.label}</option>
          ))}
        </select>
      </div>

      {/* Calculators */}
      <div style={{ ...sectionStyle, ...(isMobile ? {} : { borderRight: 'none' }) }}>
        <button
          onClick={() => onShowJoistCalc?.()}
          style={{
            ...buttonStyle(showJoistCalc),
            background: showJoistCalc ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
            borderColor: showJoistCalc ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)',
          }}
          title="Floor Joist/Truss Calculator"
        >
          <JoistCalcIcon color={showJoistCalc ? '#4caf50' : '#6080a0'} size={isMobile ? 16 : 18} />
          {!isMobile && <span>Joist Calc</span>}
        </button>
        <button
          onClick={() => onShowBeamCalc?.()}
          style={{
            ...buttonStyle(showBeamCalc),
            background: showBeamCalc ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
            borderColor: showBeamCalc ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)',
          }}
          title="Beam & Header Calculator"
        >
          <BeamCalcIcon color={showBeamCalc ? '#4caf50' : '#6080a0'} size={isMobile ? 16 : 18} />
          {!isMobile && <span>Beam Calc</span>}
        </button>
      </div>
    </div>
  );
};

export default StructuralToolbar;
